import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Alert,
} from "reactstrap";
import Loader from "../../../modules/loader";
import { postMethod } from "../../../../_services/_postMethod";

const BulkCustomerImport = ({ isOpen, toggle }) => {
  const [parsedRows, setParsedRows] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // ✅ Required Columns (must match Excel exactly)
  const REQUIRED_COLUMNS = [
    "S.No.",
    "Mobile",
    "Full Name",
    "Gender",
    "Address",
    "Profession",
    "Email",
    "Spouse's Birthday", 
    "Dob",
    "Doa",
    "Category",
  ];

  // ✅ Convert any date value to YYYY-MM-DD string
  const formatToYYYYMMDD = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (isNaN(date)) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`; // Always string
  };

  // 📂 Handle Excel Upload
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setMessage("");
    setShowPreview(false);
    setUploadResult(null);

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        // ✅ Important: raw false to get formatted date
        const parsed = XLSX.utils.sheet_to_json(sheet, {
          defval: "",
          raw: false,
        });

        if (!parsed.length) {
          setMessage("⚠️ No data found in the Excel file.");
          return;
        }

        const cleaned = parsed.map((row) => {
          const filtered = {};

          REQUIRED_COLUMNS.forEach((col) => {
            let value = row[col] ?? "";

            // ✅ Force Dob & Doa into YYYY-MM-DD string
            if (col === "Dob" || col === "Doa" || col === "Spouse's Birthday") {
              value = formatToYYYYMMDD(value);
            }

            filtered[col] = value;
          });

          return filtered;
        });

        setParsedRows(cleaned);
        setMessage(`✅ ${cleaned.length} rows loaded successfully.`);
      } catch (error) {
        console.error("Excel parse error:", error);
        setMessage("❌ Failed to parse 6Excel file. Please check format.");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // 👁 Preview First 5 Rows
  const handlePreview = () => {
    if (!parsedRows.length) {
      setMessage("⚠️ Please upload a valid Excel file first.");
      return;
    }

    setPreviewRows(parsedRows.slice(0, 5));
    setShowPreview(true);
  };

  // 📥 Download Sample Template
  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL}/customer.xlsx`;
    link.download = "customer_sample.xlsx";
    link.click();
  };

  // 🚀 Upload to API
  const handleUpload = async () => {
    if (!parsedRows.length) {
      setMessage("⚠️ Please upload a valid Excel file first.");
      return;
    }

    setLoading(true);
    setMessage("⏳ Uploading data...");
    setUploadResult(null);

    try {
      const payload = { customers: parsedRows };

      const res = await postMethod(
        `api/masters/customer/bulk_customer`,
        payload
      );

      const apiData = res?.data?.data?.[0] || {};
      const details = apiData?.details || [];

      setUploadResult(details);
      setMessage("✅ Upload completed successfully.");
    } catch (error) {
      console.error("Upload error:", error);
      setMessage("❌ Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // 🧹 Reset & Close
  const handleClose = () => {
    setParsedRows([]);
    setPreviewRows([]);
    setUploadResult(null);
    setMessage("");
    setShowPreview(false);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
      <ModalHeader toggle={handleClose}>
        Bulk Customer Import
      </ModalHeader>

      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {loading && <Loader />}

        <div className="d-flex flex-wrap gap-2 mb-3">
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFile}
            disabled={loading}
          />

          <Button
            color="secondary"
            onClick={handlePreview}
            disabled={!parsedRows.length || loading}
            style={{ marginRight: "0.5rem" }}
          >
            Preview
          </Button>

          <Button color="info" onClick={handleDownloadSample}>
            Download Sample
          </Button>
        </div>

        {message && (
          <Alert color={message.includes("❌") ? "danger" : "info"}>
            {message}
            
          </Alert>
        )}

        {/* Preview Table */}
        {showPreview && previewRows.length > 0 && (
          <div className="table-responsive">
            <Table bordered size="sm">
              <thead>
                <tr>
                  {REQUIRED_COLUMNS.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i}>
                    {REQUIRED_COLUMNS.map((key) => (
                      <td key={key}>{row[key]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
            <small className="text-muted">
              Showing first 5 rows
            </small>
          </div>
        )}

        {/* Upload Result */}
        {uploadResult && uploadResult.length > 0 && (
          <div className="mt-3">
            <h6>Upload Summary:</h6>
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Mobile</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {uploadResult.slice(0, 10).map((r, i) => (
                  <tr key={i}>
                    <td>{r.FullName || r.full_name || "—"}</td>
                    <td>{r.Mobile || r.mobile || "—"}</td>
                    <td>{r.error ? "❌ Failed" : "✅ Success"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <small className="text-muted">
              Showing first 10 upload results
            </small>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          color="primary"
          onClick={handleUpload}
          disabled={loading || !parsedRows.length}
        >
          {loading ? "Uploading..." : "Upload"}
        </Button>

        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkCustomerImport;